import * as Yup from 'yup';
import { 
  DIFFICULTY_LEVELS_ARRAY, 
  ANSWER_OPTIONS_ARRAY, 
  DIFFICULTY_LEVELS,
  ANSWER_OPTIONS,
  type DifficultyLevel,
  type AnswerOption 
} from '@/constants/assessment';

// Assessment validation schema
export const assessmentValidationSchema = Yup.object({
  title: Yup.string()
    .required('Assessment title is required')
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must not exceed 100 characters'),
  
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must not exceed 500 characters'),
  
  category: Yup.string()
    .required('Category is required')
    .min(1, 'Category cannot be empty')
    .max(100, 'Category must not exceed 100 characters'),
  
  difficulty: Yup.string()
    .required('Difficulty is required')
    .oneOf(DIFFICULTY_LEVELS_ARRAY, 'Invalid difficulty level'),
  
  configuration: Yup.object({
    questionCount: Yup.number()
      .required('Number of questions is required')
      .min(10, 'Must have at least 10 question')
      .max(100, 'Cannot exceed 100 questions')
      .integer('Must be a whole number'),
    
    timeLimit: Yup.number()
      .required('Time limit is required')
      .min(1, 'Time limit must be at least 1 minute')
      .max(300, 'Time limit cannot exceed 300 minutes')
      .integer('Must be a whole number'),
    
    passCriteria: Yup.number()
      .required('Pass criteria is required')
      .min(1, 'Pass criteria must be at least 1%')
      .max(100, 'Pass criteria cannot exceed 100%')
      .integer('Must be a whole number'),
    
    questionOrder: Yup.string()
      .required('Question order is required')
      .oneOf(['random', 'sequential'], 'Invalid question order'),
    
    retakePolicy: Yup.object({
      allowRetakes: Yup.boolean().required('Retake policy is required'),
      
      maxAttempts: Yup.number()
        .required('Max attempts is required')
        .min(1, 'Must allow at least 1 attempt')
        .max(10, 'Cannot exceed 10 attempts')
        .integer('Must be a whole number'),
      
      waitingPeriod: Yup.number()
        .required('Waiting period is required')
        .min(0, 'Waiting period cannot be negative')
        .max(168, 'Waiting period cannot exceed 168 hours (1 week)')
        .integer('Must be a whole number'),
    }).required('Retake policy is required'),
  }).required('Configuration is required'),
  
  availabilityWindow: Yup.object({
    startDate: Yup.string().optional(),
    endDate: Yup.string().optional(),
  }).optional(),
});

// Question validation schema
export const questionValidationSchema = Yup.object({
  question: Yup.string()
    .required('Question text is required')
    .min(10, 'Question must be at least 10 characters')
    .max(1000, 'Question must not exceed 1000 characters'),
  
  options: Yup.object({
    optionA: Yup.string()
      .required('Option A is required')
      .min(1, 'Option A cannot be empty')
      .max(200, 'Option A must not exceed 200 characters'),
    
    optionB: Yup.string()
      .required('Option B is required')
      .min(1, 'Option B cannot be empty')
      .max(200, 'Option B must not exceed 200 characters'),
    
    optionC: Yup.string()
      .required('Option C is required')
      .min(1, 'Option C cannot be empty')
      .max(200, 'Option C must not exceed 200 characters'),
    
    optionD: Yup.string()
      .required('Option D is required')
      .min(1, 'Option D cannot be empty')
      .max(200, 'Option D must not exceed 200 characters'),
  }).required('All options are required'),
  
  correctAnswer: Yup.string()
    .required('Correct answer is required')
    .oneOf(ANSWER_OPTIONS_ARRAY, 'Correct answer must be A, B, C, or D'),
  
  category: Yup.string()
    .required('Category is required')
    .min(1, 'Category cannot be empty')
    .max(100, 'Category must not exceed 100 characters'),
  
  difficulty: Yup.string()
    .required('Difficulty is required')
    .oneOf(DIFFICULTY_LEVELS_ARRAY, 'Invalid difficulty level'),
  
  tags: Yup.array()
    .of(Yup.string().min(1, 'Tag cannot be empty'))
    .max(10, 'Cannot have more than 10 tags'),
  
  explanation: Yup.string()
    .max(500, 'Explanation must not exceed 500 characters'),
});

// Category validation schema
export const categoryValidationSchema = Yup.object({
  name: Yup.string()
    .required('Category name is required')
    .min(2, 'Category name must be at least 2 characters')
    .max(100, 'Category name must not exceed 100 characters')
    .matches(/^[a-zA-Z0-9\s-_]+$/, 'Category name can only contain letters, numbers, spaces, hyphens, and underscores'),
  
  description: Yup.string()
    .max(500, 'Description must not exceed 500 characters'),
  
  isActive: Yup.boolean()
    .required('Active status is required'),
});

// Initial values for forms
export const initialAssessmentValues = {
  title: '',
  description: '',
  category: '', // Will be populated from API or user selection
  difficulty: DIFFICULTY_LEVELS.BEGINNER as DifficultyLevel,
  configuration: {
    questionCount: 20,
    timeLimit: 60,
    passCriteria: 70,
    questionOrder: 'random' as 'random' | 'sequential',
    retakePolicy: {
      allowRetakes: true,
      maxAttempts: 3,
      waitingPeriod: 24,
    },
  },
  questions: [] as string[],
  availabilityWindow: {
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
};

export const initialQuestionValues = {
  question: '',
  options: {
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
  },
  correctAnswer: ANSWER_OPTIONS.A as AnswerOption,
  category: '', // Will be populated from API or user selection
  difficulty: DIFFICULTY_LEVELS.BEGINNER as DifficultyLevel,
  tags: [] as string[],
  explanation: '',
};

// Initial values for category form
export const initialCategoryValues = {
  name: '',
  description: '',
  isActive: true,
};